/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function SidenavCollapse({
  icon,
  name,
  active,
  badge,
  badgeColor = "error",
  description,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, darkMode, sidenavColor } = controller;
  // Matches Sidenav: the white sidenav is a light-mode look.
  const whiteSidenav = controller.whiteSidenav && !darkMode;

  return (
    <ListItem component="li">
      <MDBox
        {...rest}
        sx={(theme) => ({
          ...collapseItem(theme, {
            active,
            transparentSidenav,
            whiteSidenav,
            darkMode,
            sidenavColor,
          }),
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateX(5px)",
            backgroundColor: active
              ? undefined
              : transparentSidenav || whiteSidenav
              ? "rgba(0, 0, 0, 0.05)"
              : "rgba(255, 255, 255, 0.1)",
          },
        })}
      >
        <ListItemIcon
          sx={(theme) =>
            collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active })
          }
        >
          {typeof icon === "string" ? (
            <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
          ) : (
            icon
          )}
        </ListItemIcon>

        <ListItemText
          primary={
            <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <span style={{ color: transparentSidenav || whiteSidenav ? "#344767" : "#ffffff" }}>
                {name}
              </span>
              {badge && !miniSidenav && (
                <Chip
                  label={badge}
                  size="small"
                  color={badgeColor}
                  sx={{
                    height: "20px",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    ml: 1,
                  }}
                />
              )}
            </MDBox>
          }
          secondary={!miniSidenav && description ? description : undefined}
          sx={(theme) =>
            collapseText(theme, {
              miniSidenav,
              transparentSidenav,
              whiteSidenav,
              active,
            })
          }
          secondaryTypographyProps={{
            noWrap: true,
            sx: {
              fontSize: "0.7rem",
              opacity: 0.7,
              mt: 0.5,
              color: transparentSidenav || whiteSidenav ? "#344767" : "#ffffff",
            },
          }}
        />
      </MDBox>
    </ListItem>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  badgeColor: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error"]),
  description: PropTypes.string,
};

export default SidenavCollapse;
